import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * ChatbotConnex component that loads and manages the Connex chatbot script.
 * It retrieves and dynamically injects the chatbot script.
 */

const ChatbotConnex = () => {
	const channelSettings = useSelector(({ fuse }) => fuse.form.channelSettings?.value) || {};

	// Function to extract socket URI from the Connex URL
	const getSocketUri = url => {
		try {
			const urlObj = new URL(url);
			return `${urlObj.origin}`;
		} catch (error) {
			console.error('Invalid Connex URL:', error);
			return null;
		}
	};

	// Extract Connex URL and ID from channel settings & and parse the settings to get the socket URI
	const { connexURL, connexId, socketUri } = useMemo(() => {
		const companyServices = channelSettings?.channelServices || [];
		const connexService = companyServices.find(service => service?.companyService?.serviceType === 5) || {};

		try {
			const channelServiceSettings = connexService?.channelServiceSettings?.settings
				? JSON.parse(connexService.channelServiceSettings.settings || '{}')
				: {};

			const url = channelServiceSettings?.url?.trim();
			const id = channelServiceSettings?.connex_id;

			return {
				connexURL: url,
				connexId: id,
				socketUri: url ? getSocketUri(url) : null
			};
		} catch (error) {
			return { connexURL: null, connexId: null, socketUri: null };
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [channelSettings]);

	useEffect(() => {
		if (!connexURL || !connexId) {
			return;
		}

		// Preventing multiple script injections if the script is already present
		if (document.getElementById(connexId)) {
			return;
		}

		const script = document.createElement('script');
		script.src = connexURL;
		script.id = connexId;
		script.async = true;

		if (socketUri) {
			script.dataset.socketUri = socketUri;
		}

		script.onerror = () => {
			document.body.removeChild(script);
		};

		document.body.appendChild(script);

		return () => {
			const existingScript = document.getElementById(connexId);
			if (existingScript) {
				document.body.removeChild(existingScript);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connexURL, connexId, socketUri]);

	return null;
};

export default ChatbotConnex;
